import React from 'react';
import { motion } from 'framer-motion';

interface SkeletonLoaderProps {
  variant?: 'text' | 'circular' | 'rectangular' | 'card' | 'list' | 'profile';
  width?: string | number;
  height?: string | number;
  lines?: number;
  className?: string;
  animated?: boolean;
  count?: number;
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  variant = 'rectangular',
  width,
  height,
  lines = 3,
  className = '',
  animated = true,
  count = 1
}) => {
  // Animation variants
  const shimmerVariants = {
    initial: { backgroundPosition: '-200px 0' },
    animate: {
      backgroundPosition: '200px 0',
      transition: {
        duration: 1.5,
        ease: 'linear' as const,
        repeat: Infinity
      }
    }
  };

  const pulseVariants = {
    initial: { opacity: 0.6 },
    animate: {
      opacity: [0.6, 1, 0.6],
      transition: {
        duration: 1.5,
        ease: 'easeInOut' as const,
        repeat: Infinity
      }
    }
  };

  const getBaseClasses = () => 
    `bg-gray-200 dark:bg-gray-700 ${animated ? 'bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 bg-[length:200px_100%]' : ''}`;

  const renderSkeleton = (key?: number) => {
    switch (variant) {
      case 'text':
        return (
          <div key={key} className="space-y-2">
            {Array.from({ length: lines }).map((_, index) => (
              <motion.div
                key={index}
                className={`h-4 rounded ${getBaseClasses()} ${index === lines - 1 ? 'w-3/4' : 'w-full'} ${className}`}
                style={{ width: index === lines - 1 ? '75%' : width, height }}
                variants={animated ? shimmerVariants : pulseVariants}
                initial="initial"
                animate="animate"
              />
            ))}
          </div>
        );

      case 'circular':
        return (
          <motion.div
            key={key}
            className={`rounded-full ${getBaseClasses()} ${className}`}
            style={{ 
              width: width || height || '40px', 
              height: height || width || '40px' 
            }}
            variants={animated ? shimmerVariants : pulseVariants}
            initial="initial"
            animate="animate"
          />
        );

      case 'rectangular':
        return (
          <motion.div
            key={key}
            className={`rounded ${getBaseClasses()} ${className}`}
            style={{ width: width || '100%', height: height || '20px' }}
            variants={animated ? shimmerVariants : pulseVariants}
            initial="initial"
            animate="animate"
          />
        );

      case 'card':
        return (
          <div key={key} className={`space-y-3 p-4 border rounded-lg ${className}`}>
            <motion.div
              className={`h-4 rounded w-3/4 ${getBaseClasses()}`}
              variants={animated ? shimmerVariants : pulseVariants}
              initial="initial"
              animate="animate"
            />
            <motion.div
              className={`h-32 rounded ${getBaseClasses()}`}
              variants={animated ? shimmerVariants : pulseVariants}
              initial="initial"
              animate="animate"
            />
            <div className="space-y-2">
              <motion.div
                className={`h-3 rounded w-full ${getBaseClasses()}`}
                variants={animated ? shimmerVariants : pulseVariants}
                initial="initial"
                animate="animate"
              />
              <motion.div
                className={`h-3 rounded w-5/6 ${getBaseClasses()}`}
                variants={animated ? shimmerVariants : pulseVariants}
                initial="initial"
                animate="animate"
              />
            </div>
          </div>
        );

      case 'list':
        return (
          <div key={key} className={`space-y-3 ${className}`}>
            {Array.from({ length: lines }).map((_, index) => (
              <div key={index} className="flex items-center space-x-3">
                <motion.div
                  className={`w-10 h-10 rounded-full ${getBaseClasses()}`}
                  variants={animated ? shimmerVariants : pulseVariants}
                  initial="initial"
                  animate="animate"
                />
                <div className="space-y-2 flex-1">
                  <motion.div
                    className={`h-4 rounded w-3/4 ${getBaseClasses()}`}
                    variants={animated ? shimmerVariants : pulseVariants}
                    initial="initial"
                    animate="animate"
                  />
                  <motion.div
                    className={`h-3 rounded w-1/2 ${getBaseClasses()}`}
                    variants={animated ? shimmerVariants : pulseVariants}
                    initial="initial"
                    animate="animate"
                  />
                </div>
              </div>
            ))}
          </div>
        );

      case 'profile':
        return (
          <div key={key} className={`space-y-4 ${className}`}>
            <div className="flex items-center space-x-4">
              <motion.div
                className={`w-16 h-16 rounded-full ${getBaseClasses()}`}
                variants={animated ? shimmerVariants : pulseVariants}
                initial="initial"
                animate="animate"
              />
              <div className="space-y-2">
                <motion.div
                  className={`h-4 rounded w-32 ${getBaseClasses()}`}
                  variants={animated ? shimmerVariants : pulseVariants}
                  initial="initial"
                  animate="animate"
                />
                <motion.div
                  className={`h-3 rounded w-24 ${getBaseClasses()}`}
                  variants={animated ? shimmerVariants : pulseVariants}
                  initial="initial"
                  animate="animate"
                />
              </div>
            </div>
            <div className="space-y-2">
              <motion.div
                className={`h-3 rounded w-full ${getBaseClasses()}`}
                variants={animated ? shimmerVariants : pulseVariants}
                initial="initial"
                animate="animate"
              />
              <motion.div
                className={`h-3 rounded w-5/6 ${getBaseClasses()}`}
                variants={animated ? shimmerVariants : pulseVariants}
                initial="initial"
                animate="animate"
              />
              <motion.div
                className={`h-3 rounded w-4/6 ${getBaseClasses()}`}
                variants={animated ? shimmerVariants : pulseVariants}
                initial="initial"
                animate="animate"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (count === 1) {
    return renderSkeleton();
  }

  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, index) => renderSkeleton(index))}
    </div>
  );
};

export default SkeletonLoader;